export interface IncomingMessage {
	readonly [key: string]: unknown;
	readonly type: string;
}

export interface MessageListenerOptions {
	readonly expectedOrigin: string;
	readonly iframe: HTMLIFrameElement;
	readonly onMessage: (message: IncomingMessage) => void;
}

// Both checks are non-negotiable:
//  - `event.origin` proves the message came from the widget origin (a different
//    iframe on the page can't forge it – browsers set it from the sender's
//    actual origin).
//  - `event.source === iframe.contentWindow` proves it came from OUR widget
//    instance, not a sibling iframe on the same origin (e.g. an ad).
// Drop anything that fails either check silently – we don't want a noisy
// console for unrelated messages, but we also don't want to leak that we're
// listening.
export const installMessageListener = (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLIFrameElement nested in options is an external mutable DOM type
	options: MessageListenerOptions,
): (() => void) => {
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- MessageEvent is an external mutable DOM interface
	const handler = (event: MessageEvent): void => {
		if (event.origin !== options.expectedOrigin) {
			return;
		}
		if (event.source !== options.iframe.contentWindow) {
			return;
		}
		const data = event.data as unknown;
		if (typeof data !== "object" || data === null) {
			return;
		}
		// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- narrowing `object` further to `IncomingMessage` after typeof checks
		const message = data as IncomingMessage;
		if (typeof message.type !== "string") {
			return;
		}
		options.onMessage(message);
	};
	window.addEventListener("message", handler);
	return () => {
		window.removeEventListener("message", handler);
	};
};

// Outbound sends always pass an explicit target origin – never `"*"`. If the
// iframe hasn't mounted yet `contentWindow` is null; we silently no-op so the
// public API can be called eagerly without timing checks at the call site.
export const postToWidget = (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLIFrameElement is an external mutable DOM interface
	iframe: HTMLIFrameElement,
	origin: string,
	message: Readonly<Record<string, unknown>>,
): void => {
	iframe.contentWindow?.postMessage(message, origin);
};
